import { z } from 'zod';
/**
 * How a whole-document discount is applied — separate concept from per-line-item
 * discounts.
 */
export const DocumentDiscountTypeSchema = z.enum(['NONE', 'PERCENTAGE', 'FIXED']);
/**
 * Rejects a PERCENTAGE discount over 100%. FIXED has no schema-level upper bound — it's
 * clamped at the applicable subtotal ({@link computeDocumentDiscountAmount} for whole
 * documents, {@link computeLineItemSubtotal} for line items) and surfaced as an inline UI
 * warning instead of a hard validation error. Shared by both whole-document and
 * per-line-item discount schemas — {@link DocumentDiscountType} and `LineItemDiscountType`
 * are structurally identical unions, so one function covers both.
 */
export function isDocumentDiscountWithinBounds(data) {
    return data.discountType !== 'PERCENTAGE' || (data.discount ?? 0) <= 100;
}
export const DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE = {
    error: 'Discount percentage cannot exceed 100%',
    path: ['discount'],
};
/**
 * Computes the dollar amount of a whole-document discount. FIXED is clamped at
 * `lineItemsSubtotal` so the document total can never go negative, mirroring the
 * `Math.max(0, gross - discount)` floor in {@link computeLineItemSubtotal}.
 */
export function computeDocumentDiscountAmount(lineItemsSubtotal, discount, discountType) {
    if (discountType === 'PERCENTAGE')
        return lineItemsSubtotal * (discount / 100);
    if (discountType === 'FIXED')
        return Math.min(discount, lineItemsSubtotal);
    return 0;
}
/**
 * Pro-rates a whole-document discount across taxable line items before computing tax, so
 * `taxAmount` reflects the post-discount base instead of the inflated pre-discount one —
 * required to keep GST/BAS reporting figures correct. Mirrors the tax-after-discount
 * convention {@link computeLineItemSubtotal} already applies per line item. Zero line
 * items naturally yields a zero proration (no divide-by-zero) since both the discount
 * amount and the subtotal evaluate to zero against an empty base.
 */
export function computeDocumentTotals(lineItems, taxRate, discount, discountType) {
    const subtotal = lineItems.reduce((sum, lineItem) => sum + lineItem.subtotal, 0);
    const discountAmount = computeDocumentDiscountAmount(subtotal, discount, discountType);
    const proration = subtotal > 0 ? discountAmount / subtotal : 0;
    const taxAmount = lineItems
        .filter((lineItem) => lineItem.taxable)
        .reduce((sum, lineItem) => sum + lineItem.subtotal * (1 - proration) * taxRate, 0);
    return {
        subtotal,
        discountAmount,
        taxAmount,
        total: subtotal - discountAmount + taxAmount,
    };
}
//# sourceMappingURL=document-discount.js.map